<template>
  <div v-if="submitted" class="success">
    <h2>
      <span class="icon"><i class="fal fa-check-circle"></i></span>
      {{ successHeadline }}
    </h2>
    <p>{{ successBodyInterpolated }}</p>
  </div>
  <div class="t-linkify" v-else>
    <validation-observer v-slot="{ invalid }" ref="form">
      <form @submit.prevent="handleSubmit">
        <h3 class="headline">{{ $translations.wholesale.create }}</h3>
        <div class="inputs">
          <FlInputWithValidation
            rules="required"
            v-model="form.first_name"
            name="first-name"
            type="text"
            :label="`${$translations.customer.addresses.first_name}*`"
          />

          <FlInputWithValidation
            rules="required"
            v-model="form.last_name"
            name="last-name"
            type="text"
            :label="`${$translations.customer.addresses.last_name}*`"
          />

          <FlInputWithValidation
            rules="required|email"
            v-model="form.email"
            name="email"
            type="email"
            :label="`${$translations.customer.login.email}*`"
          />

          <FlInputWithValidation
            rules="required"
            v-model="form.business_name"
            name="business-name"
            type="text"
            :label="`${$translations.customer.addresses.company}*`"
          />

          <div>
            <label for="business-type"
              >{{ $translations.wholesale.business_type }}*</label
            >
            <puori-select v-model="form.business_type" small id="business-type">
              <option v-for="type in types" :key="type" :value="type">
                {{ type }}
              </option>
            </puori-select>
          </div>

          <FlInputWithValidation
            rules="required"
            v-model="form.business_phone"
            name="business-phone"
            type="tel"
            :label="`${$translations.customer.addresses.phone}*`"
          />

          <FlInputWithValidation
            rules="required"
            v-model="form.business_address"
            name="business-address"
            type="text"
            :label="`${$translations.customer.addresses.address1}*`"
          />

          <FlInputWithValidation
            rules="required"
            v-model="form.business_city"
            name="business-city"
            type="text"
            :label="`${$translations.customer.addresses.city}*`"
          />

          <FlInputWithValidation
            rules="required"
            v-model="form.business_zip"
            name="business-zip"
            type="text"
            :label="`${$translations.customer.addresses.zip}*`"
          />

          <div>
            <label for="business-country"
              >{{ $translations.customer.addresses.country }}*</label
            >
            <puori-select
              v-model="form.business_country"
              small
              id="business-country"
            >
              <option
                v-for="(country, index) in countries"
                :key="index"
                :value="country.value"
              >
                {{ country.name }}
              </option>
            </puori-select>
          </div>

          <div v-if="provinces.length > 0">
            <label for="business-province"
              >{{ $translations.customer.addresses.province }}*</label
            >
            <puori-select
              v-model="form.business_province"
              small
              id="business-province"
            >
              <option
                v-for="(province, index) in provinces"
                :key="index"
                :value="province[0]"
              >
                {{ province[1] }}
              </option>
            </puori-select>
          </div>

          <FlInputWithValidation
            v-if="!us"
            rules=""
            v-model="form.vat"
            name="business-vat"
            type="text"
            :label="`${$translations.wholesale.vat}*`"
          />

          <FlInputWithValidation
            v-if="us"
            :rules="{ required: resaleCertificateNumberRequired }"
            v-model="form.resale_certificate_number"
            name="resale-certificate-number"
            type="text"
            :label="`${$translations.wholesale.resale_certificate_number}*`"
            ref="numberValidation"
          />

          <div>
            <label for="file-upload">{{
              $translations.wholesale.resale_certificate_file
            }}</label>
            <puori-upload-input-with-validation
              id="file-upload"
              v-if="us"
              :rules="{
                ext: ['pdf', 'png', 'jpg', 'doc', 'docx'],
                required: resaleCertificateFileRequired
              }"
              v-model="form.resale_certificate_file"
              ref="fileValidation"
            />
          </div>

          <puori-checkbox-with-validation
            id="termsandconditions"
            name="terms-and-conditions"
            :rules="{ required: { allowFalse: false } }"
            v-model="form.terms_and_conditions"
          >
            <span
              v-html="termsAndConditions.replace('<p>', '').replace('</p>', '')"
            />
          </puori-checkbox-with-validation>

          <puori-checkbox-with-validation
            rules=""
            v-model="form.newsletter"
            name="newsletter"
            id="newsletter"
          >
            <span v-html="newsletter.replace('<p>', '').replace('</p>', '')" />
          </puori-checkbox-with-validation>
        </div>
        <div class="submit">
          <button
            type="submit"
            class="button"
            :disabled="invalid || submitting"
          >
            {{ $translations.wholesale.submit }}
          </button>
        </div>
        <ul v-if="errors.length > 0" class="errors">
          <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
        </ul>
      </form></validation-observer
    >
  </div>
</template>

<script>
import {
  ValidationProvider,
  ValidationObserver,
  withValidation,
  localize
} from "vee-validate/dist/vee-validate.full";
import FlInput from "./fl-input.vue";
import PuoriUploadInput from "./puori-upload-input.vue";
import PuoriSelect from "./puori-select.vue";
import PuoriCheckbox from "./puori-checkbox.vue";

localize({
  en: {
    names: {},
    messages: {
      required: window.ShopifyTranslations.validations.required,
      email: window.ShopifyTranslations.validations.email
    }
  }
});

const FlInputWithValidation = withValidation(FlInput, ctx => ({
  error: ctx.errors[0]
}));

const PuoriUploadInputWithValidation = withValidation(
  PuoriUploadInput,
  ctx => ({
    error: ctx.errors[0]
  })
);

const PuoriCheckboxWithValidation = withValidation(PuoriCheckbox, ctx => ({
  error: ctx.errors[0]
}));

export default {
  components: {
    FlInputWithValidation,
    ValidationProvider,
    ValidationObserver,
    PuoriUploadInputWithValidation,
    PuoriSelect,
    PuoriCheckboxWithValidation
  },
  name: "WholesaleForm",
  selector: "#vue-wholesale-form",
  props: {
    us: {
      type: Boolean,
      default: false
    },
    countriesSelector: {
      type: String,
      required: true
    },
    successHeadline: {
      type: String,
      required: true
    },
    successBody: {
      type: String,
      required: true
    },
    termsAndConditions: {
      type: String,
      required: true
    },
    newsletter: {
      type: String,
      required: true
    },
    countryCode: {
      type: String,
      default: "DK"
    }
  },
  data() {
    return {
      rawErrors: null,
      form: {
        first_name: "",
        last_name: "",
        email: "",
        business_name: "",
        business_type: "Personal Trainer",
        business_phone: "",
        business_address: "",
        business_city: "",
        business_country: "",
        business_province: "",
        business_zip: "",
        vat: "",
        resale_certificate_number: "",
        resale_certificate_file: undefined,
        terms_and_conditions: false,
        newsletter: false
      },
      countries: [],
      provinces: [],
      types: [
        "Personal Trainer",
        "CrossFit Box",
        "Independent/Specialist Gym",
        "Clinic/Recovery Center",
        "Health/Fitness/Yoga",
        "Retail",
        "Webshop",
        "Other"
      ],
      submitting: false,
      submitted: false
    };
  },
  computed: {
    resaleCertificateNumberRequired() {
      if (this.form.resale_certificate_file) return false;
      return true;
    },
    resaleCertificateFileRequired() {
      if (this.form.resale_certificate_number !== "") return false;
      return true;
    },
    successBodyInterpolated() {
      return this.successBody
        .replace(/({{\s*first_name\s*}})/, this.form.first_name)
        .replace(/({{\s*last_name\s*}})/, this.form.last_name);
    },
    errors() {
      if (!this.rawErrors) return [];
      return Object.values(this.rawErrors).reduce(
        (acc, errors) => acc.concat(errors),
        []
      );
    }
  },
  watch: {
    "form.resale_certificate_number"() {
      this.$refs.fileValidation.validate();
      this.$nextTick(() => {
        this.$refs.numberValidation.validate();
      });
    },
    "form.resale_certificate_file"() {
      this.$refs.fileValidation.validate();
      this.$nextTick(() => {
        this.$refs.numberValidation.validate();
      });
    },
    "form.business_country"() {
      if (this.form.business_country === "") {
        this.provinces = [];
        this.form.business_province = "";
        return;
      }
      const country = this.countries.find(
        country => country.value === this.form.business_country
      );
      if (!country) {
        this.provinces = [];
        this.form.business_province = "";
        return;
      }

      this.provinces = country.provinces;
      this.form.business_province =
        country.provinces.length > 0 ? country.provinces[0][0] : "";
    }
  },
  mounted() {
    const countriesEl = document.querySelector(this.countriesSelector);
    if (!countriesEl) return;
    this.countries = [...countriesEl.querySelectorAll("option")].map(
      option => ({
        name: option.textContent,
        value: option.value,
        provinces: JSON.parse(option.dataset.provinces)
      })
    );

    if (this.countries.length > 0) {
      this.form.business_country = this.countries[0].value;
    }
  },
  methods: {
    async handleSubmit() {
      this.rawErrors = null;
      const validationResult = await this.$refs.form.validate();
      if (!validationResult) return;

      const formData = Object.entries(this.form).reduce(
        (formData, [key, value]) => {
          if (value) {
            if (key === "newsletter" || key === "terms_and_conditions") {
              value = value ? "1" : "0";
            }
            console.log("appending", key, value);
            formData.append(key, value);
          }
          return formData;
        },
        new FormData()
      );

      formData.append("country_code", this.countryCode);

      this.submitting = true;
      try {
        const { data } = await this.$http.post(
          "https://mailer.signifly.com/api/puori-wholesale/mail",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }
        );
        this.submitted = true;
      } catch (err) {
        if (err.response && err.response.data && err.response.data.errors) {
          this.rawErrors = err.response.data.errors;
        }
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.headline {
  margin: 0;
  font-size: 24px;
  color: $teal;
}

.inputs,
.submit {
  margin-top: 40px;
}

.inputs {
  > * + * {
    margin-top: 48px;
  }
}

.success {
  text-align: center;
  .icon {
    color: #2ecc71;
  }
}
</style>